import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import {
    DollarSign, Download, Mail, FileText, Calculator, TrendingUp,
    Settings, Users, Calendar, CheckCircle, AlertCircle, Save
} from 'lucide-react';
import { PageHeader } from '../../shared-ui/States';
import '../../shared-ui/crane-tokens.css';
import './Payroll.css';

// --- CONFIGURATION ---
const API_BASE = API_URL;
const NODE_API = API_URL;

// --- HELPER: CALCULATE TAX ---

// --- HELPER: CALCULATE TAX ---
const calculateTax = (annualIncome, regime = 'new') => {
    // Simplified Tax Logic (for demo/draft)
    let tax = 0;
    const taxable = Math.max(0, annualIncome - 75000); // Standard Deduction

    if (regime === 'new') {
        if (taxable > 300000) tax += (Math.min(taxable, 600000) - 300000) * 0.05;
        if (taxable > 600000) tax += (Math.min(taxable, 900000) - 600000) * 0.10;
        if (taxable > 900000) tax += (Math.min(taxable, 1200000) - 900000) * 0.15;
        // ... extend slabs
    }
    return tax / 12; // Monthly TDS
};

const Payroll = () => {
    // --- STATE ---
    const [view, setView] = useState('DASHBOARD'); // DASHBOARD, RUN, SETTINGS, COMPLIANCE
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [employees, setEmployees] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [payrollData, setPayrollData] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
    const [userRole, setUserRole] = useState(null);
    const [userId, setUserId] = useState(null);
    const [mySlips, setMySlips] = useState([]);
    const [complianceRules, setComplianceRules] = useState([]); // Dynamic State Rules
    const [selectedCycle, setSelectedCycle] = useState(null);
    const [cycleDetails, setCycleDetails] = useState([]);

    // Settings State
    const [settings, setSettings] = useState({
        sandwichRule: true,
        fixedDays: 'CALENDAR', // CALENDAR, 30, 26
        paidWeekends: ['Sunday'], // Days that are paid off
        pfCap: 1800,
        esiThreshold: 21000,
        pf_employee_limit: 10,
        hra_percent: 40,
        special_percent: 10,
        basic_percent: 50
    });

    const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('zTaskAuthToken')}` } });


    // --- DATA LOADING ---
    const fetchAllData = async () => {
        // Only fetch full data for Admin/HR
        if (userRole === 'EMPLOYEE') return;

        setLoading(true);
        try {
            const [empRes, leaveRes, holidayRes] = await Promise.all([
                axios.get(`${API_BASE}/hr/employees`, getAuthHeader()),
                axios.get(`${API_BASE}/leaves/requests`, getAuthHeader()),
                axios.get(`${API_BASE}/holidays`, getAuthHeader())
            ]);

            // Fix for inconsistent API returns
            const empList = empRes.data.data ? empRes.data.data : (Array.isArray(empRes.data) ? empRes.data : []);
            setEmployees(empList);
            setLeaves(leaveRes.data || []);
            setHolidays(holidayRes.data || []);

            // PRE-FETCH ATTENDANCE STATS FOR ALL EMPLOYEES
            // We do this here to avoid N+1 API calls inside the calculation loop later if possible.
            // However, the current stats endpoint is by Single User ID. 
            // We will fetch them on demand inside runPayrollInfo using Promise.all

        } catch (error) {
            console.error("Data Load Error", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('zTaskUserDetails') || '{}');
        const role = (user.role?.name || 'EMPLOYEE').toUpperCase();
        setUserRole(role);
        setUserId(user.id);

        const isPrivileged = ['SUPER_ADMIN', 'ADMIN', 'HR'].includes(role);

        if (isPrivileged) {
            fetchAllData();
            fetchStates();
        } else {
            setView('MY_SLIPS');
        }
    }, [selectedMonth]);

    // --- LOGIC ENGINE (NOW USES BACKEND) ---
    const runPayrollInfo = async () => {
        if (employees.length === 0) {
            alert("No employees found to calculate payroll.");
            return;
        }

        setLoading(true);
        setLoadingMessage(`Calculating Payroll for ${selectedMonth}... This may take a moment.`);
        try {
            // Call the new dynamic payroll calculation API
            const res = await axios.post(`${API_BASE}/payroll/calculate`, {
                monthYear: selectedMonth
            }, getAuthHeader());

            if (res.data && res.data.payrollData) {
                // Transform the backend response to match frontend format
                const computed = res.data.payrollData.map(emp => ({
                    empId: emp.empId,
                    name: emp.name,
                    code: emp.code,
                    dept: emp.department,
                    designation: emp.designation,
                    days: {
                        total: emp.days.total || 0,
                        working: emp.days.working || 0,
                        weekends: emp.days.weekends || 0,
                        holidays: emp.days.holidays || 0,
                        present: emp.days.present || 0,
                        half_days: emp.days.half_days || 0,
                        paidLeaves: emp.days.paidLeaves || 0,
                        lopLeaves: emp.days.lopLeaves || 0,
                        lopDays: emp.days.lopDays || 0,
                        payable: emp.days.payable || 0,
                        // For backward compatibility
                        leaves: emp.days.paidLeaves || 0,
                        paid: emp.days.payable || 0
                    },
                    earnings: {
                        baseGross: emp.earnings.baseGross || 0,
                        basic: emp.earnings.basic || 0,
                        hra: emp.earnings.hra || 0,
                        special: emp.earnings.special || 0,
                        medical: emp.earnings.medical || 0,
                        transport: emp.earnings.transport || 0,
                        bonus: 0
                    },
                    deductions: {
                        pf: emp.deductions.pf || 0,
                        esi: emp.deductions.esi || 0,
                        pt: emp.deductions.pt || 0,
                        tds: emp.deductions.tds || 0
                    },
                    netPay: emp.netPay || 0
                }));

                setPayrollData(computed);
                setView('RUN');
            } else {
                alert("No payroll data received from server.");
            }
        } catch (error) {
            console.error("Payroll Calculation Error:", error);
            alert("Failed to calculate payroll. " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // --- PROCESS PAYROLL (SUBMIT/APPROVE) ---
    const processPayroll = async (action) => {
        const confirmMsg = action === 'APPROVE'
            ? `Are you sure you want to APPROVE & RELEASE payroll for ${selectedMonth}? This will generate salary slips for all employees.`
            : `Are you sure you want to SUBMIT payroll for ${selectedMonth} for approval?`;

        if (!payrollData || payrollData.length === 0) return;
        if (!window.confirm(confirmMsg)) return;

        setLoading(true);
        try {
            const res = await axios.post(`${API_BASE}/payroll/freeze`, {
                month: selectedMonth,
                employees: payrollData,
                action: action
            }, getAuthHeader());

            if (res.data) {
                alert(res.data.message);
                setView('REPORTS');
                fetchHistory();
            }
        } catch (e) {
            console.error(e);
            alert("Failed to process payroll. " + (e.response?.data?.error || e.message));
        } finally {
            setLoading(false);
        }
    };

    const approveCycle = async (cycleId) => {
        if (!window.confirm("Are you sure you want to APPROVE this payroll? This will Release Slips to all employees.")) return;
        setLoading(true);
        try {
            await axios.post(`${API_BASE}/payroll/approve/${cycleId}`, {}, getAuthHeader());
            alert("Payroll Approved Successfully!");
            setView('REPORTS');
            fetchHistory();
        } catch (e) {
            console.error(e);
            alert("Approval Failed.");
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        try {
            await axios.put(`${API_BASE}/payroll/settings`, settings, getAuthHeader());
            alert("Settings Saved!");
        } catch (e) {
            console.error(e);
            alert("Failed to save settings.");
        }
    };

    // --- FETCH DYNAMIC RULES ---
    const fetchRules = async () => {
        try {
            const res = await axios.get(`${API_BASE}/payroll/rules`, getAuthHeader());
            setComplianceRules(res.data || []);
        } catch (e) { console.error("Rules Fetch Error", e); }
    };

    const [historyData, setHistoryData] = useState([]);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/payroll/history`, getAuthHeader());
            setHistoryData(res.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const saveRule = async (ruleData) => {
        try {
            await axios.put(`${API_BASE}/payroll/rules`, ruleData, getAuthHeader());
            alert("Rule Saved!");
            fetchRules(); // Refresh
            setEditingRule(null);
        } catch (e) { console.error(e); alert("Failed to save rule"); }
    };

    const [editingRule, setEditingRule] = useState(null);

    const fetchCycleDetails = async (cycle) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/payroll/history/${cycle.id}`, getAuthHeader());
            setCycleDetails(res.data || []);
            setSelectedCycle(cycle);
            setView('CYCLE_DETAILS');
        } catch (e) {
            console.error(e);
            alert("Failed to fetch cycle details.");
        } finally {
            setLoading(false);
        }
    };

    const fetchMySlips = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/payroll/my-slips`, getAuthHeader());
            setMySlips(res.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const downloadSlip = async (slipId, monthYear) => {
        try {
            const response = await axios.get(`${API_BASE}/payroll/download/${slipId}`, {
                ...getAuthHeader(),
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `SalarySlip_${monthYear}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Download Error", error);
            alert("Failed to download salary slip.");
        }
    };

    useEffect(() => {
        fetchRules();
    }, []);

    useEffect(() => {
        if (view === 'MY_SLIPS') fetchMySlips();
        if (view === 'REPORTS') fetchHistory();
        if (view === 'SETTINGS') {
            axios.get(`${API_BASE}/payroll/settings`, getAuthHeader())
                .then(res => { if (res.data.calculation_basis) setSettings(res.data); })
                .catch(console.error);
        }
    }, [view]);

    // --- STATE LOADING ---
    const fetchStates = async () => {
        try {
            const res = await axios.get(`${NODE_API}/get-column/state_master/state_name`, getAuthHeader());
            const formatted = (res.data || []).map(s => ({ value: s.id, label: s.value }));
            setStatesList(formatted);
        } catch (e) { console.error("State Fetch Error", e); }
    };

    useEffect(() => {
        fetchAllData();
        fetchStates();
    }, [selectedMonth]);

    const [statesList, setStatesList] = useState([]);

    const isAdmin = ['SUPER_ADMIN', 'ADMIN', 'HR'].includes(userRole);
    const isApprover = ['SUPER_ADMIN', 'ADMIN'].includes(userRole);
    const isHR = userRole === 'HR';
    const isSuper = ['SUPER_ADMIN', 'ADMIN'].includes(userRole); // For settings/rules

    // --- RENDER ---
    return (
        <div className="payroll-container crane-page">
            {/* --- LOADING OVERLAY --- */}
            {loading && (
                <div className="processing-overlay">
                    <div className="processing-content">
                        <div className="spinner"></div>
                        <h3>{loadingMessage || 'Processing...'}</h3>
                        <p>Please do not refresh the page.</p>
                    </div>
                </div>
            )}

            <PageHeader
                crumbs={['Operations', 'ZHR', 'Payroll']}
                title="Payroll engine"
                description="Salaries, slips, and statutory compliance from live HR API."
                actions={
                    <div className="header-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div className="user-role-badge">Role: {userRole}</div>
                        {isAdmin && (
                            <div className="month-selector">
                                <Calendar size={16} />
                                <input className="crane-input" type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} />
                            </div>
                        )}
                        {view !== 'DASHBOARD' && isAdmin && (
                            <button type="button" className="crane-btn" onClick={() => setView('DASHBOARD')}>Back to Dash</button>
                        )}
                    </div>
                }
            />

            {/* --- DASHBOARD VIEW (ADMIN/HR) --- */}
            {view === 'DASHBOARD' && isAdmin && (
                <div className="dashboard-grid">
                    <div className="action-card" onClick={runPayrollInfo}>
                        <div className="icon-box blue"><Calculator size={24} /></div>
                        <h3>Run Payroll</h3>
                        <p>Generate draft for {selectedMonth}</p>
                    </div>
                    <div className="action-card" onClick={() => setView('REPORTS')}>
                        <div className="icon-box green"><FileText size={24} /></div>
                        <h3>Reports</h3>
                        <p>History & Slips</p>
                    </div>
                    {/* Only SUPER_ADMIN or ADMIN can see Settings */}
                    {isSuper && (
                        <div className="action-card" onClick={() => setView('SETTINGS')}>
                            <div className="icon-box orange"><Settings size={24} /></div>
                            <h3>Configuration</h3>
                            <p>PF, PT, Notice Period</p>
                        </div>
                    )}
                    {isSuper && (
                        <div className="action-card" onClick={() => setView('COMPLIANCE')}>
                            <div className="icon-box blue"><TrendingUp size={24} /></div>
                            <h3>Statutory Rules</h3>
                            <p>State-wise PT & LWF</p>
                        </div>
                    )}

                    <div className="stats-row">
                        <div className="stat"><span>Last Payroll</span><strong>₹ {(historyData[0]?.total_payout || 0).toLocaleString()}</strong></div>
                        <div className="stat"><span>Active Employees</span><strong>{employees.length}</strong></div>
                    </div>
                </div>
            )}

            {/* --- MY SLIPS VIEW (EMPLOYEE) --- */}
            {view === 'MY_SLIPS' && (
                <div className="run-view">
                    <div className="run-header">
                        <h2>My Salary Slips</h2>
                        <div className="actions">
                            <span className="subtitle">View your monthly payslips</span>
                        </div>
                    </div>
                    <div className="table-responsive crane-card">
                        <table className="payroll-table crane-table">
                            <thead><tr><th>Month</th><th>Status</th><th>Net Pay</th><th>Action</th></tr></thead>
                            <tbody>
                                {mySlips.map(h => (
                                    <tr key={h.id}>
                                        <td><b>{h.month_year}</b></td>
                                        <td><span className={`status-badge ${h.cycle_status?.toLowerCase()}`}>{h.cycle_status || 'GENERATED'}</span></td>
                                        <td style={{ fontWeight: 700 }}>₹ {parseFloat(h.net_pay || 0).toLocaleString()}</td>
                                        <td>
                                            <button
                                                className="btn-outline btn-sm"
                                                title="Download PDF"
                                                onClick={() => downloadSlip(h.id, h.month_year)}
                                            >
                                                <Download size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {mySlips.length === 0 && <tr><td colSpan="4" style={{ padding: 20, textAlign: 'center' }}>No slips found.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* --- SETTINGS VIEW --- */}
            {view === 'SETTINGS' && isSuper && (
                <div className="settings-panel">
                    <h2>Company Policies</h2>
                    <div className="setting-group-row">
                        <div className="form-col">
                            <label>Calculation Basis</label>
                            <select value={settings.fixedDays} onChange={e => setSettings({ ...settings, fixedDays: e.target.value })}>
                                <option value="CALENDAR">Actual Days in Month</option>
                                <option value="30">Fixed 30 Days</option>
                                <option value="26">Fixed 26 Days (Excl Sundays)</option>
                            </select>
                        </div>
                        <div className="form-col">
                            <label>PF Threshold (Min Emp)</label>
                            <input
                                type="number"
                                value={settings.pf_employee_limit || 20}
                                onChange={e => setSettings({ ...settings, pf_employee_limit: parseInt(e.target.value) })}
                            />
                            <small className="hint-text">Deduct PF only if emp count &ge; this.</small>
                        </div>
                        <div className="form-col">
                            <label>PF % (Employee Share)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={settings.pf_employee_percent || 12}
                                onChange={e => setSettings({ ...settings, pf_employee_percent: parseFloat(e.target.value) })}
                            />
                            <small className="hint-text">Standard is 12%</small>
                        </div>
                    </div>
                    <div className="setting-group-row">
                        <div className="form-col">
                            <label>Basic % of Gross</label>
                            <input
                                type="number"
                                value={settings.basic_percent || 0}
                                onChange={e => setSettings({ ...settings, basic_percent: parseFloat(e.target.value) })}
                            />
                        </div>
                        <div className="form-col">
                            <label>HRA % of Basic</label>
                            <input
                                type="number"
                                value={settings.hra_percent || 0}
                                onChange={e => setSettings({ ...settings, hra_percent: parseFloat(e.target.value) })}
                            />
                        </div>
                        <div className="form-col">
                            <label>Special Allowance % of Basic</label>
                            <input
                                type="number"
                                value={settings.special_percent || 0}
                                onChange={e => setSettings({ ...settings, special_percent: parseFloat(e.target.value) })}
                            />
                        </div>
                    </div>
                    <div className="setting-group-row">
                        <div className="form-col">
                            <label>Default Notice Period (Days)</label>
                            <input
                                type="number"
                                value={settings.default_notice_period_days || 30}
                                onChange={e => setSettings({ ...settings, default_notice_period_days: parseInt(e.target.value) })}
                            />
                            <small className="hint-text">Used to calculate tentative LWD.</small>
                        </div>
                    </div>
                    <div className="setting-group">
                        <label>
                            <input type="checkbox" checked={settings.sandwichRule} onChange={e => setSettings({ ...settings, sandwichRule: e.target.checked })} />
                            Enable Sandwich Rule (Unpaid Weekends if LOP adjacent)
                        </label>
                    </div>
                    <button className="btn-primary" onClick={saveSettings}>Save Configuration</button>
                </div>
            )}
            {/* --- COMPLIANCE VIEW --- */}
            {view === 'COMPLIANCE' && isSuper && (
                <div className="run-view">
                    <div className="run-header">
                        <h2>Statutory Compliance Rules</h2>
                        <button className="btn-primary" onClick={() => setEditingRule({})}>+ Add State Rule</button>
                    </div>

                    {editingRule && (
                        <div className="compliance-form-overlay">
                            <div className="compliance-card swing-in-top-fwd">
                                <div className="card-header">
                                    <h3>{editingRule.id ? 'Edit Configuration' : 'New Compliance Rule'}</h3>
                                    <button className="close-btn" onClick={() => setEditingRule(null)}>×</button>
                                </div>

                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>State / Region</label>
                                        <select
                                            value={editingRule.state_name || ''}
                                            onChange={e => setEditingRule({ ...editingRule, state_name: e.target.value })}
                                            disabled={!!editingRule.id}
                                            className={!!editingRule.id ? 'disabled-input' : ''}
                                        >
                                            <option value="">Select State</option>
                                            {statesList
                                                .filter(s => !complianceRules.some(r => r.state_name === s.label) || s.label === editingRule.state_name)
                                                .map(s => (
                                                    <option key={s.value} value={s.label}>{s.label}</option>
                                                ))}
                                        </select>
                                        {!!editingRule.id && <small className="hint-text">State cannot be changed once created.</small>}
                                    </div>

                                    <div className="form-group">
                                        <label>Salary Threshold (Monthly)</label>
                                        <div className="input-icon-wrapper">
                                            <span className="currency-symbol">₹</span>
                                            <input
                                                type="number"
                                                placeholder="e.g. 15000"
                                                value={editingRule.pt_salary_threshold || ''}
                                                onChange={e => setEditingRule({ ...editingRule, pt_salary_threshold: e.target.value })}
                                            />
                                        </div>
                                        <small className="hint-text">Min. gross salary to differ PT</small>
                                    </div>

                                    <div className="form-group">
                                        <label>Standard Monthly PT</label>
                                        <div className="input-icon-wrapper">
                                            <span className="currency-symbol">₹</span>
                                            <input
                                                type="number"
                                                placeholder="e.g. 200"
                                                value={editingRule.pt_slab_monthly || ''}
                                                onChange={e => setEditingRule({ ...editingRule, pt_slab_monthly: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>February PT (Year End)</label>
                                        <div className="input-icon-wrapper">
                                            <span className="currency-symbol">₹</span>
                                            <input
                                                type="number"
                                                placeholder="e.g. 300"
                                                value={editingRule.pt_slab_feb || ''}
                                                onChange={e => setEditingRule({ ...editingRule, pt_slab_feb: e.target.value })}
                                            />
                                        </div>
                                        <small className="hint-text">Often higher in some states (e.g. MH)</small>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button className="btn-secondary" onClick={() => setEditingRule(null)}>Cancel</button>
                                    <button
                                        className="btn-primary"
                                        onClick={() => {
                                            if (!editingRule.state_name) return alert("Please select a state");
                                            saveRule(editingRule);
                                        }}
                                    >
                                        {editingRule.id ? 'Save Changes' : 'Create Rule'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="table-responsive crane-card">
                        <table className="payroll-table crane-table">
                            <thead><tr><th>State</th><th>PT Threshold</th><th>Monthly PT</th><th>Feb PT</th><th>Actions</th></tr></thead>
                            <tbody>
                                {complianceRules.map(r => (
                                    <tr key={r.id}>
                                        <td><b>{r.state_name}</b></td>
                                        <td>₹ {r.pt_salary_threshold}</td>
                                        <td>₹ {r.pt_slab_monthly}</td>
                                        <td>₹ {r.pt_slab_feb}</td>
                                        <td>
                                            <button className="btn-secondary" onClick={() => setEditingRule(r)}>Edit</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* --- REPORTS VIEW --- */}
            {view === 'REPORTS' && isAdmin && (
                <div className="run-view">
                    <div className="run-header"><h2>Payroll History</h2></div>
                    <div className="table-responsive crane-card">
                        <table className="payroll-table crane-table">
                            <thead><tr><th>Month</th><th>Status</th><th>Total Payout</th><th>Processed By</th><th>Date</th><th>Actions</th></tr></thead>
                            <tbody>
                                {historyData.map(h => (
                                    <tr key={h.id}>
                                        <td><b>{h.month_year}</b></td>
                                        <td><span className={`status-badge ${h.status}`}>{h.status}</span></td>
                                        <td style={{ fontWeight: 700 }}>₹ {parseFloat(h.total_payout).toLocaleString()}</td>
                                        <td>User #{h.processed_by || '-'}</td>
                                        <td>{new Date(h.created_at).toLocaleDateString()}</td>
                                        <td>
                                            <button className="btn-outline btn-sm" onClick={() => fetchCycleDetails(h)}>
                                                View Logs
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {historyData.length === 0 && <tr><td colSpan="6" style={{ padding: 20, textAlign: 'center' }}>No history found.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* --- CYCLE DETAILS VIEW --- */}
            {view === 'CYCLE_DETAILS' && isAdmin && (
                <div className="run-view">
                    <div className="run-header">
                        <h2>Payroll Logs: {selectedCycle?.month_year}</h2>
                        <div className="actions">
                            {isApprover && selectedCycle?.status === 'PENDING_APPROVAL' && (
                                <button className="btn-primary" onClick={() => approveCycle(selectedCycle.id)}>
                                    Approve Month
                                </button>
                            )}
                            <button className="btn-secondary" onClick={() => setView('REPORTS')}>Back to History</button>
                        </div>
                    </div>
                    <div className="table-responsive crane-card">
                        <table className="payroll-table crane-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Emp Code</th>
                                    <th>Department</th>
                                    <th>Gross</th>
                                    <th>Net Pay</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cycleDetails.map(entry => (
                                    <tr key={entry.id}>
                                        <td><b>{entry.first_name} {entry.last_name}</b></td>
                                        <td>{entry.emp_code}</td>
                                        <td>{entry.department_name}</td>
                                        <td>₹ {entry.gross_earned?.toLocaleString()}</td>
                                        <td style={{ fontWeight: 700 }}>₹ {entry.net_pay?.toLocaleString()}</td>
                                        <td>
                                            <button className="btn-outline" onClick={() => downloadSlip(entry.id, selectedCycle.month_year)}>
                                                <Download size={14} /> Slip
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* --- RUN PAYROLL VIEW --- */}
            {view === 'RUN' && isAdmin && (
                <div className="run-view">
                    <div className="run-header">
                        <h2>Draft Payroll: {selectedMonth}</h2>
                        <div className="actions">
                            <button className="btn-outline" onClick={runPayrollInfo} title="Refresh Calculation">
                                <Calculator size={16} /> Recalculate
                            </button>
                            <button className="btn-outline"><Download size={16} /> Export CSV</button>
                            {isHR && (
                                <button className="btn-primary" onClick={() => processPayroll('SUBMIT')} disabled={!payrollData || payrollData.length === 0}>
                                    <CheckCircle size={16} /> Submit for Approval
                                </button>
                            )}
                            {isApprover && (
                                <button className="btn-primary" onClick={() => processPayroll('APPROVE')} disabled={!payrollData || payrollData.length === 0}>
                                    <CheckCircle size={16} /> Approve & Release Slips
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="table-responsive crane-card">
                        <table className="payroll-table crane-table">
                            <thead>
                                <tr>
                                    <th rowSpan="2">Employee</th>
                                    <th colSpan="5" style={{ textAlign: 'center', borderBottom: '1px solid #ddd' }}>Attendance Details</th>
                                    <th colSpan="6" style={{ textAlign: 'center', borderBottom: '1px solid #ddd' }}>Earnings</th>
                                    <th colSpan="4" style={{ textAlign: 'center', borderBottom: '1px solid #ddd' }}>Deductions</th>
                                    <th rowSpan="2">Net Pay</th>
                                </tr>
                                <tr>
                                    <th>Working</th>
                                    <th>Present</th>
                                    <th>Leaves (Paid)</th>
                                    <th>LOP</th>
                                    <th>Payable</th>
                                    <th>Base Gross</th>
                                    <th>Basic</th>
                                    <th>HRA</th>
                                    <th>Special</th>
                                    <th>Others</th>
                                    <th>Gross</th>
                                    <th>PF</th>
                                    <th>PT</th>
                                    <th>ESI</th>
                                    <th>TDS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payrollData.map(emp => {
                                    const othersEarnings = (emp.earnings.medical || 0) + (emp.earnings.transport || 0);
                                    const grossEarned = emp.earnings.basic + emp.earnings.hra + emp.earnings.special + othersEarnings;

                                    return (
                                        <tr key={emp.empId}>
                                            <td>
                                                <div className="emp-cell">
                                                    <b>{emp.name}</b>
                                                    <span>{emp.code}</span>
                                                    <small style={{ fontSize: '0.75rem', color: '#666' }}>
                                                        {emp.designation}
                                                    </small>
                                                </div>
                                            </td>
                                            {/* Attendance */}
                                            <td>{emp.days.working || emp.days.total || '-'}</td>
                                            <td>
                                                {emp.days.present}
                                                {emp.days.half_days > 0 && (
                                                    <small style={{ display: 'block', fontSize: '0.7rem', color: '#666' }}>
                                                        +{emp.days.half_days}×0.5
                                                    </small>
                                                )}
                                            </td>
                                            <td>{emp.days.paidLeaves || emp.days.leaves || 0}</td>
                                            <td className={emp.days.lopDays > 0 ? 'deduction' : ''}>
                                                {emp.days.lopDays || emp.days.lopLeaves || 0}
                                            </td>
                                            <td><b>{emp.days.payable}</b></td>
                                            {/* Earnings */}
                                            <td style={{ color: '#64748B' }}>₹{(emp.earnings.baseGross || 0).toLocaleString()}</td>
                                            <td>₹{emp.earnings.basic.toLocaleString()}</td>
                                            <td>₹{emp.earnings.hra.toLocaleString()}</td>
                                            <td>₹{emp.earnings.special.toLocaleString()}</td>
                                            <td>₹{othersEarnings.toLocaleString()}</td>
                                            <td><b>₹{grossEarned.toLocaleString()}</b></td>
                                            {/* Deductions */}
                                            <td className="deduction">₹{Math.round(emp.deductions.pf).toLocaleString()}</td>
                                            <td className="deduction">₹{Math.round(emp.deductions.pt).toLocaleString()}</td>
                                            <td className="deduction">₹{Math.round(emp.deductions.esi || 0).toLocaleString()}</td>
                                            <td className="deduction">₹{Math.round(emp.deductions.tds).toLocaleString()}</td>
                                            {/* Net Pay */}
                                            <td className="net-pay">₹ {emp.netPay.toLocaleString()}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                                    <td colSpan="11" style={{ textAlign: 'center' }}>TOTAL PAYOUT:</td>
                                    <td className="net-pay" colSpan="5">
                                        ₹ {payrollData.reduce((sum, emp) => sum + emp.netPay, 0).toLocaleString()}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Payroll;

